import {AnalysisLine, analysisView} from './analysisLine';
import {PuzzleRun} from './courseLogic';

/**
 * What the student has on screen, sent with every question to the coach.
 *
 * Without it the coach answered about the puzzle's *starting* position, even
 * after the student had played half the line, and on a video chapter it had
 * no position at all. The server checks all of it before use: the FEN must
 * be a legal position and the moves must replay from the puzzle's start.
 */
export type CoachContext = {
  /** The position on the puzzle board (or in analysis, after the solve). */
  board_fen?: string | null;
  /** Every move played on the line since the puzzle started, both sides. */
  played_san?: string[];
  /**
   * The student's wrong tries. `ply` is how many moves had been played when
   * the try was made, so the server knows which position it was played in.
   */
  attempts?: {ply: number; san: string}[];
  /** Seconds into the recording, on a video chapter. */
  video_time?: number | null;
};

/**
 * The coach's view of a puzzle board.
 *
 * The line position, not the wrong move on show: a wrong move sits on the
 * board for under two seconds before it is taken back, and the student asks
 * about it as their attempt, which `attempts` carries.
 */
export function puzzleContext(
  run: PuzzleRun,
  analysis: AnalysisLine | null,
): CoachContext {
  const attempts: {ply: number; san: string}[] = [];
  Object.entries(run.mistakes ?? {})
    .sort(([a], [b]) => Number(a) - Number(b))
    .forEach(([ply, sans]) => {
      // `mistakes` is keyed by the 1-based ply of the student's move; the
      // moves before it are one fewer.
      sans.forEach(san => attempts.push({ply: Number(ply) - 1, san}));
    });
  if (analysis) {
    // After the solve the board is wherever analysis stands, which may be off
    // the puzzle's line: the moves are the analysis line's, up to there. The
    // wrong tries are left out — they were made on the puzzle's line, and
    // replayed on a line that left it they would land in the wrong position.
    return {
      board_fen: analysisView(analysis).fen,
      played_san: analysis.sans
        .slice(1, analysis.index + 1)
        .filter((san): san is string => !!san),
    };
  }
  return {
    board_fen: run.fen,
    played_san: run.history.map(h => h.san),
    attempts,
  };
}

/** The coach's view of a video chapter at `seconds` into the recording. */
export function videoContext(seconds: number | null | undefined): CoachContext {
  return typeof seconds === 'number' && Number.isFinite(seconds) && seconds >= 0
    ? {video_time: seconds}
    : {};
}
